/**
 * Confirm SetupIntent Mutation Hook
 *
 * Step 3 of the Stripe client-confirm card-save flow: POSTs the confirmed
 * SetupIntent id to the backend `/confirm` endpoint, which re-verifies it
 * server-side (succeeded + customer-owned) and persists the card. Returns the
 * DB `paymentMethodId` — the same shape the old `/token` path returned.
 *
 * Mirrors {@link useSavePaymentMethod} (tenant-scoped cache invalidation) so the
 * saved-methods list refreshes after an add, regardless of provider.
 *
 * @module payment/hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { paymentMethodService } from '~/services/internalApi/payment/api/paymentMethodService';
import type { ApiResult } from '~/lib/apiClient';
import type { ConfirmSetupIntentPayload } from '~/services/internalApi/payment/types';
import { PaymentSaveError } from '../errors';

export interface UseConfirmSetupIntentOptions {
  /** Tenant handle for precise cache invalidation (optional — see useSavePaymentMethod). */
  tenantHandle: string | undefined;
  onSuccess?: (response: ApiResult<ConfirmSetupIntentPayload>) => void;
  onError?: (error: Error) => void;
}

/**
 * Confirm-and-persist mutation for the Stripe client-confirm flow.
 *
 * @example
 * ```tsx
 * const confirmSetupIntent = useConfirmSetupIntent({ tenantHandle });
 * const { paymentMethodId } = await confirmSetupIntent.mutateAsync({ userId, setupIntentId });
 * ```
 */
export function useConfirmSetupIntent(options: UseConfirmSetupIntentOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    // Never retry: the SetupIntent was already confirmed client-side and is
    // single-use, so a retry re-sends a spent intent and can never succeed.
    retry: false,

    mutationFn: async ({
      userId,
      setupIntentId,
    }: {
      userId: number;
      setupIntentId: string;
    }) => {
      const result = await paymentMethodService.confirmSetupIntent(userId, setupIntentId);

      // Carries the API's message + code (see PaymentSaveError) rather than a
      // fixed string, so the member reads why the card was refused.
      if (!result.success) {
        throw new PaymentSaveError(result);
      }

      return result;
    },

    onSuccess: (response, variables) => {
      if (options.tenantHandle) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tenant.paymentMethods(options.tenantHandle, variables.userId),
        });
      }

      options?.onSuccess?.(response);
    },

    onError: (error: Error) => {
      console.error('[useConfirmSetupIntent] Error:', error);
      options?.onError?.(error);
    },
  });
}
